/**
 * The verified LinkedIn organisation references — the frontend's restatement.
 *
 * This is the twin of `backend/src/domain/linkedin/organization-directory.ts`, and both restate
 * `config/issuer-linkedin-organization.json`, which is where the id, its evidence and its
 * verification date actually live. Each package tests its restatement against that file, so the
 * fixture-backed demo and the real backend cannot map the same issuer to different organisations.
 *
 * The number is NOT in the URL builder and NOT in any component. It belongs to an issuer: the
 * builder takes it as an argument, the issuer's own `organizationReference` is read first, and this
 * directory answers only for the LinkedIn Page it was verified against. An issuer configured with
 * some other Page gets no id and is addressed by name — never by this organisation's number.
 *
 * Deliberately separate from `LINKEDIN_ORGANIZATION_REFERENCE` in `linkedin-integration.ts`: that
 * constant is the frozen prototype's own displayed placeholder, rendered on the settings screen and
 * measured by the pixel-parity gate. Changing what a screen prints is a design decision; wiring
 * what a link carries is not.
 */

export interface VerifiedOrganization {
  displayName: string;
  pageUrl: string;
  organizationId: string;
  urn: string;
  source: 'verified_configuration';
  verifiedAt: string;
  verifiedBy: string;
}

export const VERIFIED_ORGANIZATIONS: readonly VerifiedOrganization[] = [
  {
    displayName: 'Happy Place to Work® Türkiye',
    pageUrl: 'https://www.linkedin.com/company/happyplacetowork/',
    organizationId: '3038217',
    urn: 'urn:li:organization:3038217',
    source: 'verified_configuration',
    verifiedAt: '2026-09-12',
    verifiedBy: 'product-owner',
  },
];

/**
 * Two Page URLs name the same Page when they differ only in the ways LinkedIn itself varies them:
 * the scheme, a `www.` or locale prefix (`tr.`, `ca.`), a trailing slash, letter case, or a tracking
 * query string. Everything else is a different Page and must not match.
 */
export function samePage(a: string, b: string): boolean {
  const key = (raw: string): string | null => {
    try {
      const u = new URL(raw.trim());
      if (!/(^|\.)linkedin\.com$/.test(u.hostname)) return null;
      const path = u.pathname.replace(/\/+$/, '').toLowerCase();
      return path === '' ? null : path;
    } catch {
      return null;
    }
  };
  const ka = key(a);
  const kb = key(b);
  return ka !== null && ka === kb;
}

/** The verified numeric id for an issuer's configured LinkedIn Page, or null when there is none. */
export function verifiedOrganizationIdForPage(pageUrl: string | null | undefined): string | null {
  if (!pageUrl) return null;
  for (const org of VERIFIED_ORGANIZATIONS) if (samePage(org.pageUrl, pageUrl)) return org.organizationId;
  return null;
}
